import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { useRef } from "react";
import { Calendar } from "lucide-react";

interface DatePickerProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  /** Optional transform applied to the raw value before updating the form state */
  transform?: (value: string) => string;
  /** Minimum selectable date in YYYY-MM-DD format */
  minDate?: string;
  maxDate?: string;
}

function formatToDDMMYYYY(iso?: string) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}-${m}-${y}`;
}

export default function DatePicker<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  disabled = false,
  error,
  placeholder,
  transform,
  minDate,
  maxDate,
}: DatePickerProps<T>) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openPicker = () => {
    if (disabled) return;
    if (inputRef.current?.showPicker) {
      try {
        inputRef.current.showPicker();
      } catch {
        inputRef.current.focus();
      }
    } else {
      inputRef.current?.focus();
    }
  };

  return (
    <div>
      <label className="block text-md font-bold text-black mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="relative">
            <div
              onClick={openPicker}
              className={`w-full flex items-center justify-between px-3 py-[11px] border rounded-lg ${
                disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white cursor-pointer"
              } ${error ? "border-red-500" : "border-gray-300"}`}
            >
              <span className={field.value ? "text-gray-900" : "text-gray-400"}>
                {field.value ? formatToDDMMYYYY(field.value) : placeholder || "dd-mm-yyyy"}
              </span>
              <Calendar className="w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            <input
              {...field}
              ref={(el) => {
                field.ref(el);
                inputRef.current = el;
              }}
              type="date"
              disabled={disabled}
              placeholder={placeholder}
              min={minDate}
              max={maxDate}
              onChange={(e) => {
                const val = transform ? transform(e.target.value) : e.target.value;
                field.onChange(val);
                field.onBlur();
              }}
              className="absolute inset-0 w-full h-full opacity-0 pointer-events-none disabled:cursor-not-allowed"
              tabIndex={-1}
            />
          </div>
        )}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}